/**
 * WeaveIn.ai Platform - Contact Status Pipeline
 * Visual stepper showing a contact's progression through a program's status pipeline
 */

import React, { useState, useEffect } from 'react';
import { CheckCircle, Circle, ChevronRight, ArrowRight, Lock, Loader2, AlertCircle } from 'lucide-react';
import { contactsAPI, StatusConfigItem, ContactProgramStatus } from '@/services/api/contacts';

interface ContactStatusPipelineProps {
  contactId: number;
  programStatus: ContactProgramStatus;
  onStatusChange?: (newStatus: string) => void;
  onAdvanceError?: (error: string) => void;
}

const DEFAULT_COLORS: Record<string, string> = {
  new: '#3B82F6',
  outreach: '#6366F1',
  qualified: '#F59E0B',
  eligible: '#10B981',
  enrolled: '#059669',
  declined: '#EF4444',
  ineligible: '#6B7280',
  unreachable: '#9CA3AF',
};

export default function ContactStatusPipeline({ contactId, programStatus, onStatusChange, onAdvanceError }: ContactStatusPipelineProps) {
  const [configs, setConfigs] = useState<StatusConfigItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [advancing, setAdvancing] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const data = await contactsAPI.getStatusConfig(programStatus.program_id);
        setConfigs(data.sort((a, b) => a.sort_order - b.sort_order));
      } catch {
        setConfigs([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [programStatus.program_id]);

  const currentIdx = configs.findIndex((c) => c.status_code === programStatus.status);
  const currentConfig = configs[currentIdx];

  const handleAdvance = async (targetStatus: string) => {
    setAdvancing(true);
    try {
      await contactsAPI.updateContactStatus(contactId, programStatus.program_id, {
        new_status: targetStatus,
      });
      onStatusChange?.(targetStatus);
    } catch (err: any) {
      const msg = err.response?.data?.detail || err.message || 'Failed to advance status';
      onAdvanceError?.(msg);
    } finally {
      setAdvancing(false);
    }
  };

  if (loading) {
    return <div className="flex items-center gap-2 text-sm text-gray-400 py-4"><Loader2 className="w-4 h-4 animate-spin" /> Loading pipeline...</div>;
  }

  if (configs.length === 0) {
    return <div className="text-sm text-gray-400 py-2">No status pipeline configured for this program.</div>;
  }

  // Separate terminal and non-terminal statuses
  const mainFlow = configs.filter((c) => !c.is_terminal || c.promotes_to_enrollment);
  const terminalStatuses = configs.filter((c) => c.is_terminal && !c.promotes_to_enrollment);

  return (
    <div className="space-y-4">
      {/* Main pipeline stepper */}
      <div className="flex items-center gap-1 overflow-x-auto pb-2">
        {mainFlow.map((step, idx) => {
          const isCurrent = step.status_code === programStatus.status;
          const isPast = currentIdx >= 0 && idx < currentIdx;
          const isNext = currentConfig?.allowed_next_statuses?.includes(step.status_code);
          const color = step.status_color || DEFAULT_COLORS[step.status_code] || '#6B7280';

          return (
            <React.Fragment key={step.id}>
              {idx > 0 && <ChevronRight className="w-4 h-4 text-gray-300 flex-shrink-0" />}
              <button
                onClick={isNext && !advancing ? () => handleAdvance(step.status_code) : undefined}
                disabled={!isNext || advancing}
                className={`
                  flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all
                  ${isCurrent
                    ? 'ring-2 ring-offset-1 shadow-sm text-white'
                    : isPast
                      ? 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
                      : isNext
                        ? 'border-2 border-dashed hover:shadow-md cursor-pointer'
                        : 'bg-gray-50 dark:bg-gray-800 text-gray-400 cursor-default'
                  }
                `}
                style={
                  isCurrent
                    ? { backgroundColor: color, ringColor: color }
                    : isNext
                      ? { borderColor: color, color }
                      : undefined
                }
                title={
                  isNext
                    ? `Advance to ${step.status_label}`
                    : isCurrent
                      ? 'Current status'
                      : isPast
                        ? 'Completed'
                        : 'Not yet available'
                }
              >
                {isPast ? (
                  <CheckCircle className="w-4 h-4" />
                ) : isCurrent ? (
                  <Circle className="w-4 h-4 fill-current" />
                ) : !isNext ? (
                  <Lock className="w-3.5 h-3.5" />
                ) : (
                  <ArrowRight className="w-4 h-4" />
                )}
                {step.status_label}
              </button>
            </React.Fragment>
          );
        })}
        {advancing && <Loader2 className="w-4 h-4 text-blue-500 animate-spin ml-2" />}
      </div>

      {/* Terminal actions (decline, ineligible, unreachable) */}
      {terminalStatuses.length > 0 && currentConfig && !currentConfig.is_terminal && (
        <div className="flex items-center gap-2 pt-1">
          <span className="text-xs text-gray-400">or:</span>
          {terminalStatuses
            .filter((t) => currentConfig.allowed_next_statuses?.includes(t.status_code))
            .map((t) => (
              <button
                key={t.id}
                onClick={() => handleAdvance(t.status_code)}
                disabled={advancing}
                className="px-2.5 py-1 text-xs font-medium border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-500"
              >
                {t.status_label}
              </button>
            ))}
        </div>
      )}

      {/* Required fields hint */}
      {currentConfig && (
        <div className="text-xs text-gray-400">
          {currentConfig.required_member_fields && currentConfig.required_member_fields.length > 0 && (
            <span>Required at this stage: {currentConfig.required_member_fields.join(', ')}</span>
          )}
        </div>
      )}
    </div>
  );
}
